import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { getMemberById } from "@/modules/Users/service/UsersService"
import { MemberDetailType } from "@/types/membros"
import { useLoading } from "@/hooks/useLoading"
import { User, MapPin, Building2, Users, Star, ArrowLeft } from "lucide-react"
import { Button } from "@/components/ui/button"
import { AddToFavoritesDialog } from "../components/AddToFavoritesDialog"
import { getFavoriteGroups } from "../services/favoritesService"
import { cn } from "@/utils/utils"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { useCalendarIntegration } from "@/modules/Calendar/hooks/useCalendarIntegration"
import { toast } from "sonner"

export function MemberProfilePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [member, setMember] = useState<MemberDetailType | null>(null)
  const { showLoading, hideLoading } = useLoading()
  const [isFavoritesDialogOpen, setIsFavoritesDialogOpen] = useState(false)
  const [isFavorite, setIsFavorite] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [photoLoaded, setPhotoLoaded] = useState(false)
  const { user } = useAuth()
  const calendar = useCalendarIntegration()

  const isOwnProfile = user?.pesUid === id

  const checkIfFavorite = () => {
    getFavoriteGroups().then((groups) => {
      setIsFavorite(groups.some((g) => g.pessoas.some((p) => p.uid === id)))
    })
  }

  useEffect(() => {
    if (!id) return
    showLoading()
    getMemberById(id)
      .then(({ data }) => {
        const fetched: MemberDetailType = data.data ?? data
        if (!fetched) {
          setNotFound(true)
          return
        }
        setMember(fetched)
        checkIfFavorite()
      })
      .catch((error) => console.error("Error:", error))
      .finally(() => hideLoading())
  }, [id])

  if (!member && !notFound) return null

  if (notFound) {
    return (
      <div className="p-10 text-center">
        <h1 className="text-2xl font-bold">Membro não encontrado</h1>
        <p className="mt-4 text-zinc-500">O membro que você está procurando não existe.</p>
        <Button
          className="mt-6"
          variant="outline"
          onClick={() => navigate("/network")}
        >
          Voltar para Networking
        </Button>
      </div>
    )
  }

  const categories = [
    member!.catpDescricao && { label: member!.catpDescricao, color: "purple" },
    member!.catsDescricao && { label: member!.catsDescricao, color: "green" },
    member!.subpDescricao && { label: member!.subpDescricao, color: "blue" },
    member!.subsDescricao && { label: member!.subsDescricao, color: "amber" },
  ].filter(Boolean) as { label: string; color: string }[]

  const colorMap: Record<string, string> = {
    purple: "border-purple-500/20 bg-purple-900/30 text-purple-400",
    green: "border-green-500/20 bg-green-900/30 text-green-400",
    blue: "border-blue-500/20 bg-blue-900/30 text-blue-400",
    amber: "border-amber-500/20 bg-amber-900/30 text-amber-400",
  }

  return (
    <div className="min-h-screen bg-black p-4 text-zinc-100 md:p-8">
      <main className="mx-auto max-w-3xl space-y-4">
        {/* Card de Identidade */}
        <div className="relative overflow-hidden rounded-3xl border border-zinc-800 bg-zinc-900 shadow-2xl">
          <div className="h-24 bg-gradient-to-r from-zinc-800 to-zinc-900" />

          <div className="px-6 pb-8">
            <div className="relative -mt-12 flex flex-col items-center gap-6 md:flex-row md:items-end md:justify-between">
              <div className="flex flex-col items-center gap-4 md:flex-row md:items-end">
                <div className="size-28 overflow-hidden rounded-2xl border-4 border-zinc-900 bg-zinc-800 shadow-xl">
                  {member!.pesFotoUrl ? (
                    <>
                      {!photoLoaded && <div className="h-full w-full animate-pulse bg-zinc-700" />}
                      <img
                        src={member!.pesFotoUrl}
                        alt={member!.pesNome}
                        onLoad={() => setPhotoLoaded(true)}
                        className={cn(
                          "h-full w-full object-cover object-top transition-opacity duration-500",
                          photoLoaded ? "opacity-100" : "absolute inset-0 opacity-0"
                        )}
                      />
                    </>
                  ) : (
                    <User
                      size={112}
                      className="p-4 text-zinc-600"
                    />
                  )}
                </div>

                <div className="text-center md:text-left">
                  <h1 className="text-2xl font-bold tracking-tight">{member!.pesNome}</h1>
                  {(member!.pesCidade || member!.pesEstado) && (
                    <p className="mt-1 flex items-center justify-center gap-1 text-sm text-zinc-400 md:justify-start">
                      <MapPin size={13} />
                      {member!.pesCidade} — {member!.pesEstado}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  className="flex size-12 items-center justify-center rounded-xl bg-zinc-800 transition-colors hover:bg-yellow-500/20"
                  onClick={() => setIsFavoritesDialogOpen(true)}
                >
                  <Star className={`text-yellow-500 ${isFavorite ? "fill-current" : ""}`} />
                </Button>
                {member!.pesTelefone && (
                  <a
                    href={`https://wa.me/${member!.pesTelefone}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex size-12 items-center justify-center rounded-xl bg-zinc-800 transition-colors hover:bg-green-500/20"
                  >
                    <i className="fi fi-brands-whatsapp flex items-center justify-center text-2xl text-green-500" />
                  </a>
                )}

                {member!.pesInstagram && (
                  <a
                    href={member!.pesInstagram}
                    target="_blank"
                    rel="noreferrer"
                    className="flex size-12 items-center justify-center rounded-xl bg-zinc-800 transition-colors hover:bg-pink-500/20"
                  >
                    <i className="fi fi-brands-instagram flex items-center justify-center text-2xl text-pink-500" />
                  </a>
                )}

                {member!.pesLinkedin && (
                  <a
                    href={member!.pesLinkedin}
                    target="_blank"
                    rel="noreferrer"
                    className="flex size-12 items-center justify-center rounded-xl bg-zinc-800 transition-colors hover:bg-blue-500/20"
                  >
                    <i className="fi fi-brands-linkedin flex items-center justify-center text-2xl text-blue-400" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Empresa */}
        <section className="rounded-3xl border border-zinc-800 bg-zinc-900/50 p-6 md:p-8">
          <div className="mb-6 flex items-center gap-3">
            <Building2
              size={16}
              className="text-zinc-500"
            />
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">
              Empresa
            </span>
            <hr className="flex-1 border-zinc-800" />
          </div>

          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex gap-4">
              {member!.empLogoUrl && (
                <img
                  src={member!.empLogoUrl}
                  alt={member!.empFantasia}
                  className="size-16 flex-shrink-0 rounded-xl border border-zinc-800 object-cover shadow-lg"
                />
              )}
              <div className="space-y-3">
                <h3 className="text-xl font-bold">{member!.empFantasia}</h3>

                {(member!.empCidade || member!.empEstado) && (
                  <p className="flex items-center gap-1.5 text-sm text-zinc-400">
                    <MapPin size={13} />
                    {member!.empCidade} — {member!.empEstado}
                  </p>
                )}

                {categories.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {categories.map((cat) => (
                      <span
                        key={cat.label}
                        className={`rounded-full border px-3 py-1 text-xs font-medium ${colorMap[cat.color]}`}
                      >
                        {cat.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {member!.empNumeroFuncionarios != null && (
              <div className="flex flex-col items-center justify-center rounded-2xl bg-zinc-800/40 px-6 py-4 text-center">
                <div className="flex items-center gap-2">
                  <Users
                    size={14}
                    className="text-zinc-500"
                  />
                  <span className="text-3xl font-black text-white">
                    {member!.empNumeroFuncionarios}
                  </span>
                </div>
                <span className="mt-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Funcionários
                </span>
              </div>
            )}
          </div>
        </section>

        <Button
          className="h-12 w-full rounded-2xl font-bold"
          variant="secondary"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft
            size={16}
            className="mr-2"
          />
          Voltar
        </Button>
        {isOwnProfile && (
          <section className="hidden rounded-3xl border border-zinc-800 bg-zinc-900/50 p-6 md:block md:p-8">
            <div className="mb-6 flex items-center gap-3">
              <hr className="flex-1 border-zinc-800" />
              <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                Integrações de Calendário
              </span>
              <hr className="flex-1 border-zinc-800" />
            </div>

            <p className="mb-4 text-sm text-zinc-400">
              Conecte seus calendários para sincronizar eventos automaticamente ao se inscrever.
            </p>

            <div className="space-y-3">
              {/* Google Calendar */}
              <div className="flex items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-800/30 p-4">
                <div className="flex items-center gap-3">
                  <svg
                    viewBox="0 0 24 24"
                    className="size-6 shrink-0"
                    fill="none"
                  >
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-white">Google Calendar</p>
                    <p className="text-xs text-zinc-500">
                      {calendar.googleConnected ? "Conta conectada" : "Não conectado"}
                    </p>
                  </div>
                </div>
                {calendar.googleConnected ? (
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-400">
                      ativo
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={calendar.disconnectGoogle}
                      className="h-7 px-2 text-xs text-zinc-400 hover:text-red-400"
                    >
                      Desconectar
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={calendar.loadingGoogle}
                    onClick={async () => {
                      try {
                        await calendar.connectGoogle()
                        toast.success("Google Calendar conectado!")
                      } catch (err: any) {
                        toast.error(err.message || "Erro ao conectar Google Calendar")
                      }
                    }}
                    className="shrink-0 border-zinc-700 bg-transparent text-white hover:bg-zinc-700"
                  >
                    {calendar.loadingGoogle ? (
                      <div className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      "Conectar"
                    )}
                  </Button>
                )}
              </div>

              {/* Microsoft Calendar */}
              <div className="flex items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-800/30 p-4">
                <div className="flex items-center gap-3">
                  <svg
                    viewBox="0 0 23 23"
                    className="size-6 shrink-0"
                  >
                    <path
                      fill="#f3f3f3"
                      d="M0 0h23v23H0z"
                    />
                    <path
                      fill="#f35325"
                      d="M1 1h10v10H1z"
                    />
                    <path
                      fill="#81bc06"
                      d="M12 1h10v10H12z"
                    />
                    <path
                      fill="#05a6f0"
                      d="M1 12h10v10H1z"
                    />
                    <path
                      fill="#ffba08"
                      d="M12 12h10v10H12z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-white">Microsoft Calendar</p>
                    <p className="text-xs text-zinc-500">
                      {calendar.microsoftConnected ? "Conta conectada" : "Não conectado"}
                    </p>
                  </div>
                </div>
                {calendar.microsoftConnected ? (
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-400">
                      ativo
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={calendar.disconnectMicrosoft}
                      className="h-7 px-2 text-xs text-zinc-400 hover:text-red-400"
                    >
                      Desconectar
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={calendar.loadingMicrosoft}
                    onClick={async () => {
                      try {
                        await calendar.connectMicrosoft()
                        toast.success("Microsoft Calendar conectado!")
                      } catch (err: any) {
                        toast.error(err.message || "Erro ao conectar Microsoft Calendar")
                      }
                    }}
                    className="shrink-0 border-zinc-700 bg-transparent text-white hover:bg-zinc-700"
                  >
                    {calendar.loadingMicrosoft ? (
                      <div className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      "Conectar"
                    )}
                  </Button>
                )}
              </div>
            </div>
          </section>
        )}

          <AddToFavoritesDialog
            isOpen={isFavoritesDialogOpen}
            onClose={() => setIsFavoritesDialogOpen(false)}
            memberUid={id}
            onFavoritesChange={checkIfFavorite}
          />
      </main>
    </div>
  )
}
